import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, Video } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { SectionLabel } from "@/components/Status";
import { LOCALE, useLang } from "@/i18n";
import { api, errCode } from "@/lib/api";

const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("");

export default function Contacts() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [people, setPeople] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/calls/contacts").then((r) => setPeople(r.data)).catch((e) => { setPeople([]); toast.error(t(`errors.${errCode(e)}`, errCode(e))); });
  }, [t]);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (people || []).filter((p) => !s || p.name.toLowerCase().includes(s) || p.email.toLowerCase().includes(s));
  }, [people, q]);

  const call = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post("/calls", { to_user_id: p.id });
      navigate(`/room/${data.meeting_id}?call=${data.id}`);
    } catch (e) { toast.error(t("contacts.callFailed")); setBusy(""); }
  };

  return (
    <div className="min-h-screen" data-testid="contacts-page">
      <Nav />
      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="reveal mb-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="mono-label mb-3">{t("contacts.eyebrow")}</div>
            <h1 className="text-4xl font-semibold tracking-tight text-vox-text sm:text-5xl">{t("contacts.title")}</h1>
          </div>
          <label className="relative block w-full sm:w-80">
            <Search size={14} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-vox-dim" />
            <input className="input !pl-10" placeholder={t("contacts.search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="contacts-search-input" />
          </label>
        </div>
        <SectionLabel index="01">{t("org.members")}</SectionLabel>
        {people === null ? (
          <div className="flex items-center gap-2 text-sm text-vox-muted"><Loader2 size={14} className="animate-spin" />{t("common.loading")}</div>
        ) : list.length === 0 ? (
          <div className="panel p-10 text-center text-sm text-vox-muted" data-testid="contacts-empty">{t("contacts.empty")}</div>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="contacts-list">
            {list.map((p, i) => (
              <li key={p.id} className={`panel reveal reveal-${Math.min(i + 1, 4)} flex items-center gap-4 p-5`} data-testid={`contact-card-${p.id}`}>
                {p.picture ? <img src={p.picture} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" /> : (
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-vox-accent/40 bg-vox-deep font-mono text-sm text-vox-bright">{initials(p.name)}</span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-vox-text" data-testid="contact-name">{p.name}</div>
                  <div className="truncate text-xs text-vox-dim">{p.email}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-vox-dim">
                    {t(`org.roles.${p.role}`)} · {p.last_login_at ? `${t("contacts.lastSeen")} ${new Date(p.last_login_at).toLocaleDateString(LOCALE[lang])}` : t("contacts.never")}
                  </div>
                </div>
                <button type="button" onClick={() => call(p)} disabled={!!busy} className="btn-primary !px-3" title={t("contacts.call")} data-testid={`contact-call-button-${p.id}`}>
                  {busy === p.id ? <Loader2 size={14} className="animate-spin" /> : <Video size={14} />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
